/**
 * Shared Static-Offer listing-duration purchase flows.
 *
 * Phase 18 Round 2B: split into two exported helpers.
 *
 *   1. purchaseListingFreeTier — the 7d free listing. Just calls
 *      `/listing/purchase` with duration_days=7; no wallet involved.
 *      Kept for the Wizard's first-listing flow + the "Renew for 7
 *      days" affordance in RenewListingDialog. The old Razorpay
 *      shape is preserved on the free tier because there's nothing
 *      to pay for.
 *
 *   2. payListingWithWallet — the 15d/30d paid tiers. Debits the
 *      vendor's wallet via `/listing/pay-with-wallet` and resolves
 *      with the invoice number + listing expiry. Callers await this
 *      and receive `{ ok, listing_expires_at, invoice_number }`.
 *      Insufficient balance is signalled by a 402 with
 *      `detail.code = "insufficient_wallet_balance"` — the caller
 *      is expected to render the WalletPayPanel "short by" state.
 *
 * The legacy Razorpay-based purchaseListingDuration function is
 * DELETED — Phase 18 Round 2C will remove the Razorpay endpoints
 * entirely, and no caller is on that path any more.
 */
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Free-tier 7d listing. No wallet debit, no Razorpay handshake.
 *
 * @param {Object} opts
 * @param {string}  opts.campaignId
 * @param {boolean} [opts.isRenewal] — cosmetic; toast copy differs.
 */
export async function purchaseListingFreeTier({ campaignId, isRenewal = false }) {
  const r = await api.listingPurchase({
    campaign_id: campaignId,
    duration_days: 7,
    coupon_code: null,
  });
  const d = r.data;
  if (!d?.free) {
    // Server didn't identify this as a free-tier response — treat
    // as an unexpected state so the UI shows a helpful error.
    throw new Error(d?.error || "Listing purchase failed");
  }
  toast.success(
    isRenewal
      ? "Listing renewed for 7 days."
      : "Listed on Explore for 7 days.",
  );
  return d;
}

/**
 * Paid-tier 15d / 30d listing — wallet debit.
 *
 * Callers should read the returned invoice_number + expiry to update
 * their UI. On a 402 insufficient_wallet_balance the caller's error
 * boundary + shared WalletPayPanel handle the "short by X" state.
 *
 * @param {Object} opts
 * @param {string} opts.campaignId
 * @param {number} opts.durationDays  — 15 | 30
 * @param {string} [opts.couponCode]
 * @param {boolean} [opts.isRenewal]  — cosmetic; toast copy differs.
 */
export async function payListingWithWallet({
  campaignId,
  durationDays,
  couponCode = null,
  isRenewal = false,
}) {
  const r = await api.listingPayWithWallet({
    campaign_id: campaignId,
    duration_days: Number(durationDays),
    coupon_code: couponCode || null,
  });
  const d = r.data;
  toast.success(
    `Listing ${isRenewal ? "renewed" : "active"} — expires ${new Date(
      d.listing_expires_at,
    ).toLocaleDateString()}.`,
  );
  return d;
}
